import { useState, useContext, useEffect } from "react";
import { FornecedoresContext } from "../../../contexts/fornecedoresProvider";
import { ClientContext } from "../../../contexts/clientProvider";
import { AnexosContext } from "../../../contexts/anexosProvider";
import { UserContext } from "../../../contexts/userProvider";
import { BiX } from "react-icons/bi";
import BaseSelect from "../../General/FormsComp/Select/BaseSelect";

const EquipamentoEdit = ({
  equipamento,
  salvarEquipamento,
  closeEditModal,
  marcas,
  modelos,
}) => {
  const { fornecedores } = useContext(FornecedoresContext);
  const { clientes } = useContext(ClientContext);
  const { anexosByClient } = useContext(AnexosContext);
  const { setAlert } = useContext(UserContext);
  
  // Estado local para gerenciar as mudanças sem fechar o modal
  const [equipamentoLocal, setEquipamentoLocal] = useState({
    ...equipamento,
    fornecedor: equipamento.fornecedor || null,
    marca: equipamento.marca || null,
    modelo: equipamento.modelo || null,
    cliente: equipamento.cliente || null,
    anexo: equipamento.anexo || null
  });
  
  const [newfornecedores, setNewFornecedores] = useState([]);
  const [newModelos, setNewModelos] = useState([]);
  const [newMarcas, setNewMarcas] = useState([]);
  const [newClientes, setNewClientes] = useState([]);
  const [newAnexos, setNewAnexos] = useState([]);

  useEffect(() => {
    let newFornecedores = fornecedores ? fornecedores : [];
    setNewFornecedores(newFornecedores);
  }, [fornecedores]);

  useEffect(() => {
    let newMarcas = marcas ? marcas : [];
    setNewMarcas(newMarcas);
  }, [marcas]);

  useEffect(() => {
    let newModelos = modelos ? modelos : [];
    setNewModelos(newModelos);
  }, [modelos]);

  useEffect(() => {
    let newClientes = clientes ? clientes : [];
    setNewClientes(newClientes);
  }, [clientes]);

  // Buscar anexos quando um cliente for selecionado
  useEffect(() => {
    const buscarAnexos = async () => {
      if (equipamentoLocal.cliente) {
        try {
          const anexos = await anexosByClient(equipamentoLocal.cliente);
          setNewAnexos(anexos || []);
        } catch (error) {
          console.error('Erro ao buscar anexos:', error);
          setNewAnexos([]);
        }
      } else {
        setNewAnexos([]);
      }
    };

    buscarAnexos();
  }, [equipamentoLocal.cliente, anexosByClient]);

  // Atualizar estado local quando o equipamento prop mudar
  useEffect(() => {
    setEquipamentoLocal({
      ...equipamento,
      fornecedor: equipamento.fornecedor || null,
      marca: equipamento.marca || null,
      modelo: equipamento.modelo || null,
      cliente: equipamento.cliente || null,
      anexo: equipamento.anexo || null
    });
  }, [equipamento]);

  // Verificar se há mudanças não salvas
  const temMudancas = 
    equipamentoLocal.fornecedor !== equipamento.fornecedor ||
    equipamentoLocal.marca !== equipamento.marca ||
    equipamentoLocal.modelo !== equipamento.modelo ||
    equipamentoLocal.cliente !== equipamento.cliente ||
    equipamentoLocal.anexo !== equipamento.anexo;

  const handleSalvar = () => {
    salvarEquipamento(equipamentoLocal);
  };

  const handleSelectFornecedor = (e) => {
    const { value } = e.target;
    const fornecedorId = parseInt(value) || null;
    
    // Atualizar apenas o estado local
    setEquipamentoLocal(prevEquipamento => ({
      ...prevEquipamento,
      fornecedor: fornecedorId,
      // Limpar marca e modelo se o fornecedor mudar
      marca: prevEquipamento.fornecedor !== fornecedorId ? null : prevEquipamento.marca,
      modelo: prevEquipamento.fornecedor !== fornecedorId ? null : prevEquipamento.modelo
    }));
  };

  const handleSelectMarca = (e) => {
    const { value } = e.target;
    const marcaId = parseInt(value) || null;
    
    let marca = null;
    if (marcaId) {
      marca = newMarcas.find((marca) => marca.id === marcaId);
    }
    
    // Atualizar estado local
    setEquipamentoLocal(prevEquipamento => {
      const novoFornecedor = marca && marca.id_fornecedor !== prevEquipamento.fornecedor 
        ? marca.id_fornecedor 
        : prevEquipamento.fornecedor;
      
      // Verificar se o modelo atual pertence à nova marca
      const modeloAtualValido = prevEquipamento.modelo && marcaId &&
        newModelos.find(m => m.id === prevEquipamento.modelo && m.id_marca === marcaId);
        
      return {
        ...prevEquipamento,
        fornecedor: novoFornecedor,
        marca: marcaId,
        // Limpar modelo se marca mudou e modelo atual não é válido para a nova marca
        modelo: modeloAtualValido ? prevEquipamento.modelo : null
      };
    });
  };

  const handleSelectModelo = (e) => {
    const { value } = e.target;
    const modeloId = parseInt(value) || null;
    
    let modelo = null;
    let marcaDoModelo = null;
    
    if (modeloId) {
      modelo = newModelos.find((modelo) => modelo.id === modeloId);
      if (modelo) {
        marcaDoModelo = newMarcas.find(marca => marca.id === modelo.id_marca);
      }
    }
    
    // Atualizar estado local
    setEquipamentoLocal(prevEquipamento => ({
      ...prevEquipamento,
      fornecedor: marcaDoModelo ? marcaDoModelo.id_fornecedor : prevEquipamento.fornecedor,
      marca: modelo ? modelo.id_marca : prevEquipamento.marca,
      modelo: modeloId
    }));
  };

  const handleSelectCliente = (e) => {
    const { value } = e.target;
    const clienteId = parseInt(value) || null;
    
    // Atualizar estado local e limpar anexo quando cliente mudar
    setEquipamentoLocal(prevEquipamento => ({
      ...prevEquipamento,
      cliente: clienteId,
      anexo: null // Limpar anexo quando cliente mudar
    }));
  };

  const handleSelectAnexo = (e) => {
    const { value } = e.target;
    const anexoId = parseInt(value) || null;
    
    setEquipamentoLocal(prevEquipamento => ({
      ...prevEquipamento,
      anexo: anexoId
    }));
  };

  // Mostrar todas as marcas, mas filtrar modelos pela marca selecionada
  const marcasFiltradas = newMarcas;
  const modelosFiltrados = equipamentoLocal.marca 
    ? newModelos.filter(modelo => modelo.id_marca === equipamentoLocal.marca)
    : newModelos;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => closeEditModal()}></div>
      
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl h-[calc(85vh-5rem)] flex flex-col">
        <BiX onClick={() => closeEditModal()} className="absolute top-4 right-4 w-8 h-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors duration-200 z-10" />
        
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white pr-10">Classificar Equipamento</h1>
          {temMudancas && (
            <p className="text-base text-orange-600 dark:text-orange-400 mt-1">
              ⚠️ Há alterações não salvas
            </p>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* Coluna 1: Informações do Equipamento Original */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                Dados do Equipamento
              </h3>
              
              <div className="space-y-3 text-base">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Número de Série:</span>
                  <p className="text-gray-900 dark:text-white font-medium text-sm mt-1 break-words">{equipamentoLocal.nserie}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">MAC:</span>
                  <p className="text-gray-900 dark:text-white text-sm mt-1 break-words">{equipamentoLocal.nmac || 'Não informado'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Etiqueta:</span>
                  <p className="text-gray-900 dark:text-white font-medium text-sm mt-1 break-words">{equipamentoLocal.netiqueta}</p>
                </div>
              </div>
            </div>

            {/* Coluna 2: Formulário de Edição */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Classificação do Equipamento
              </h3>

              {/* Cliente */}
              <div>
                <BaseSelect
                  label="Cliente *"
                  name="cliente"
                  value={equipamentoLocal.cliente || ''}
                  onChange={handleSelectCliente}
                  options={newClientes || []}
                  placeholder="Selecione o cliente"
                  valueKey="id"
                  labelKey="nome"
                  required
                />
              </div>

              {/* Anexo */}
              <div>
                <BaseSelect
                  label="Anexo"
                  name="anexo"
                  value={equipamentoLocal.anexo || ''}
                  onChange={handleSelectAnexo}
                  options={newAnexos || []}
                  placeholder={!equipamentoLocal.cliente ? "Selecione um cliente primeiro" : "Selecione o anexo"}
                  valueKey="id"
                  labelKey="numeroanexo"
                  disabled={!equipamentoLocal.cliente}
                />
              </div>

              {/* Fornecedor */}
              <div>
                <BaseSelect
                  label="Fornecedor"
                  name="fornecedor"
                  value={equipamentoLocal.fornecedor || ''}
                  onChange={handleSelectFornecedor}
                  options={newfornecedores || []}
                  placeholder="Selecione o fornecedor"
                  valueKey="id"
                  labelKey="nome"
                />
              </div>

              {/* Marca */}
              <div>
                <BaseSelect
                  label="Marca"
                  name="marca"
                  value={equipamentoLocal?.marca || ''}
                  onChange={handleSelectMarca}
                  options={marcasFiltradas || []}
                  placeholder="Selecione a marca"
                  valueKey="id"
                  labelKey="nome"
                />
              </div>

              {/* Modelo */}
              <div>
                <BaseSelect
                  label="Modelo"
                  name="modelo"
                  value={equipamentoLocal.modelo || ''}
                  onChange={handleSelectModelo}
                  options={modelosFiltrados || []}
                  placeholder={!equipamentoLocal.marca ? "Selecione uma marca primeiro" : "Selecione o modelo"}
                  valueKey="id"
                  labelKey="nome"
                  disabled={!equipamentoLocal.marca}
                />
              </div>

              {/* Resumo Compacto */}
              {(equipamentoLocal.cliente || equipamentoLocal.fornecedor || equipamentoLocal.marca || equipamentoLocal.modelo || equipamentoLocal.anexo) && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-base">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2 text-sm">Selecionado:</h4>
                  <div className="space-y-1">
                    {equipamentoLocal.cliente && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Cliente:</strong> {newClientes?.find(c => c.id === parseInt(equipamentoLocal.cliente))?.nome}
                      </p>
                    )}
                    {equipamentoLocal.anexo && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Anexo:</strong> {newAnexos?.find(a => a.id === parseInt(equipamentoLocal.anexo))?.numeroanexo}
                      </p>
                    )}
                    {equipamentoLocal.fornecedor && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Fornecedor:</strong> {newfornecedores?.find(f => f.id === parseInt(equipamentoLocal.fornecedor))?.nome}
                      </p>
                    )}
                    {equipamentoLocal.marca && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Marca:</strong> {newMarcas?.find(m => m.id === parseInt(equipamentoLocal.marca))?.nome}
                      </p>
                    )}
                    {equipamentoLocal.modelo && (
                      <p className="text-green-800 dark:text-green-200 text-sm truncate">
                        <strong>Modelo:</strong> {newModelos?.find(m => m.id === parseInt(equipamentoLocal.modelo))?.nome}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={() => closeEditModal()}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200 text-base"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            className={`px-4 py-2 font-medium rounded-lg transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 text-base ${
              temMudancas 
                ? 'bg-orange-600 hover:bg-orange-700 text-white focus:ring-orange-500' 
                : 'bg-vox-green-600 hover:bg-vox-green-700 text-white focus:ring-vox-green-500'
            }`}
          >
            {temMudancas ? 'Salvar Alterações' : 'Salvar Classificação'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EquipamentoEdit;
